import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CopyButton } from "@/pages/Applications";
import { ArrowLeft, AppWindow, Globe, Eye, EyeSlash, Trash, FloppyDisk } from "@phosphor-icons/react";

export default function ApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [reveal, setReveal] = useState({});

  const refresh = () => api.get(`/applications/${id}`).then(r => setApp(r.data)).catch(() => setApp(false));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (app === false) return <div className="text-sm text-slate-500">Application not found.</div>;
  if (!app) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/applications" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#FF6B00]" data-testid="back-to-apps">
          <ArrowLeft size={12} /> All applications
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#FFF3E0] text-[#FF6B00]">
              <AppWindow size={28} weight="duotone" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold">{app.name}</h1>
              <a href={app.website} target="_blank" rel="noreferrer" className="text-sm text-slate-500 hover:text-[#FF6B00] flex items-center gap-1">
                <Globe size={12}/> {app.website}
              </a>
            </div>
          </div>
          <Link to={`/authorize-builder?app=${app.id}`} className="btn-primary rounded-md px-4 py-2.5 text-sm font-medium" data-testid="goto-authorize-builder">
            Build authorize URL →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {app.clients?.map(c => (
          <div key={c.client_id} className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid={`client-card-${c.env}`}>
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c.env === "production" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FFF3E0] text-[#FF6B00]"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${c.env === "production" ? "bg-[#10B981]" : "bg-[#FF6B00]"}`} />
                {c.env} client
              </span>
              <span className="text-[10px] text-slate-400 font-mono">created {new Date(c.created_at).toLocaleDateString()}</span>
            </div>

            <div className="mt-4 space-y-3">
              <KV label="Client ID" value={c.client_id} testId={`client-id-${c.env}`} />
              <KV label="Client secret" value={reveal[c.client_id] ? c.client_secret : "•".repeat(32)} mono raw={c.client_secret} testId={`client-secret-${c.env}`}>
                <button onClick={() => setReveal(p => ({ ...p, [c.client_id]: !p[c.client_id] }))} className="text-slate-500 hover:text-[#FF6B00]" data-testid={`reveal-secret-${c.env}`}>
                  {reveal[c.client_id] ? <EyeSlash size={14}/> : <Eye size={14}/>}
                </button>
              </KV>
            </div>

            <RedirectUriEditor app={app} client={c} onUpdated={refresh} />
          </div>
        ))}
      </div>
    </div>
  );
}

function KV({ label, value, mono, children, raw, testId }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className={`flex-1 truncate rounded-md bg-slate-50 px-3 py-2 text-xs ${mono ? "font-mono" : ""} text-slate-800`} data-testid={testId}>{value}</code>
        <CopyButton value={raw || value} testId={`copy-${testId}`} />
        {children}
      </div>
    </div>
  );
}

function RedirectUriEditor({ app, client, onUpdated }) {
  const [uris, setUris] = useState((client.redirect_uris || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const list = uris.split("\n").map(s => s.trim()).filter(Boolean);
      await api.put(`/applications/${app.id}/clients/${client.client_id}/redirect-uris`, { redirect_uris: list });
      toast.success("Redirect URIs updated");
      onUpdated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 rounded-md bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Redirect URIs</div>
      <textarea value={uris} onChange={(e) => setUris(e.target.value)} rows={3} data-testid={`redirect-uris-${client.env}`}
        className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-mono outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15" />
      <div className="mt-2 flex items-center justify-between">
        <p className="text-[10px] text-slate-500">{client.env === "production" ? "Must use HTTPS." : "Any URI allowed (incl. localhost)."} One per line.</p>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-[#0F172A] px-3 py-1.5 text-[11px] font-medium text-white hover:bg-slate-800 disabled:opacity-60" data-testid={`save-redirect-${client.env}`}>
          <FloppyDisk size={12} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
